import { useCallback, useSyncExternalStore, useEffect, useMemo, useState } from 'react';
import { HabitTagMapEntity, TodoTagEntity } from '../types/data';
import { tagData, createTag } from '../data/tagData';
import { tagRepo } from '../utils/repos/tagRepo';
import { habitTagRepo } from '../utils/repos/habitTagRepo';
import { TODO_SYNC_STATE_KEYS, todoLocalRepo } from '../utils/localDb/todoLocalRepo';

const STORAGE_KEY = 'tags';
const HABIT_TAG_MAP_STORAGE_KEY = 'habitTagMap';
const BACKEND = (process.env.REACT_APP_BACKEND || 'local').toLowerCase();

// 保存されたタグデータを解析
const parseStoredTags = (storedData: string): TodoTagEntity[] => {
  try {
    const parsed = JSON.parse(storedData);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed as TodoTagEntity[];
  } catch {
    return [];
  }
};

// ローカルストレージからタグデータを取得
const loadTagsFromStorage = (): TodoTagEntity[] => {
  if (BACKEND === 'supabase') return [];
  const storedData = localStorage.getItem(STORAGE_KEY);
  if (!storedData) {
    return tagData; // デフォルトデータを使用
  }
  const storedTags = parseStoredTags(storedData);
  return storedTags.length > 0 ? storedTags : tagData;
};

// ローカルストレージにタグデータを保存
const saveTagsToStorage = (tags: TodoTagEntity[]) => {
  if (BACKEND === 'supabase') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tags));
  } catch (error) {
    console.error('Failed to save tags to localStorage:', error);
  }
};

const loadHabitTagMapFromStorage = (): HabitTagMapEntity[] => {
  if (BACKEND === 'supabase') return [];
  const storedData = localStorage.getItem(HABIT_TAG_MAP_STORAGE_KEY);
  if (!storedData) return [];
  try {
    const parsed = JSON.parse(storedData);
    return Array.isArray(parsed) ? parsed as HabitTagMapEntity[] : [];
  } catch {
    return [];
  }
};

const saveHabitTagMapToStorage = (mappings: HabitTagMapEntity[]) => {
  if (BACKEND === 'supabase') return;
  try {
    localStorage.setItem(HABIT_TAG_MAP_STORAGE_KEY, JSON.stringify(mappings));
  } catch (error) {
    console.error('Failed to save habitTagMap to localStorage:', error);
  }
};

export interface UseTagsReturn {
  /** アクティブなタグ一覧 */
  activeTags: TodoTagEntity[];
  /** 新しいタグを作成 */
  createNewTag: (title: string, body?: string) => Promise<TodoTagEntity> | TodoTagEntity;
  /** 並び順を変更（永続化） */
  reorderTags: (sourceId: string, targetId: string, insertAfter: boolean) => Promise<void>;
  /** タグを更新 */
  updateTag: (id: string, updates: Partial<TodoTagEntity>) => void;
  /** タグを削除 */
  deleteTag: (id: string) => void;
  /** IDでタグを取得 */
  getTagById: (id: string) => TodoTagEntity | undefined;
  /** 複数のIDでタグを取得 */
  getTagsByIds: (ids: string[]) => TodoTagEntity[];
  /** Habit に紐づく既定タグID一覧 */
  getHabitTagIds: (habitId: string) => string[];
  /** Habit に紐づく既定タグID一覧を更新 */
  setHabitTagIds: (habitId: string, tagIds: string[]) => void;
  refreshFromServer: () => void;
}

//
// External store for tags (shared across hook instances)
//
type TagsListener = () => void;
let tagsState: TodoTagEntity[] = loadTagsFromStorage();
let tagsLoadedFromSupabase = false;
let habitTagMapState: HabitTagMapEntity[] = loadHabitTagMapFromStorage();
const subscribers = new Set<TagsListener>();

const emit = () => {
  subscribers.forEach((fn) => {
    try { fn(); } catch (e) { /* noop */ }
  });
};

const getSnapshot = () => tagsState;
const subscribe = (listener: TagsListener) => {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
};

const setTagsStore = (next: TodoTagEntity[] | ((prev: TodoTagEntity[]) => TodoTagEntity[])) => {
  tagsState = typeof next === 'function' ? (next as (p: TodoTagEntity[]) => TodoTagEntity[])(tagsState) : next;
  saveTagsToStorage(tagsState);
  emit();
};

const cacheTags = (tags: TodoTagEntity[]): void => {
  if (BACKEND !== 'supabase') return;
  todoLocalRepo.upsertTags(tags)
    .catch((e) => console.error('Failed to cache tags in IndexedDB:', e));
};

// Sync with other tabs (storage event does not fire in same tab)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      tagsState = loadTagsFromStorage();
      emit();
    }
    if (e.key === HABIT_TAG_MAP_STORAGE_KEY) {
      habitTagMapState = loadHabitTagMapFromStorage();
      emit();
    }
  });
}

export const useTags = (): UseTagsReturn => {
  const tags = useSyncExternalStore(subscribe, getSnapshot, () => []);
  const [syncRequest, setSyncRequest] = useState(0);
  const refreshFromServer = useCallback(() => {
    if (BACKEND === 'supabase') {
      tagsLoadedFromSupabase = false;
      setSyncRequest(previous => previous + 1);
    }
  }, []);

  // Load from supabase once per session when enabled
  useEffect(() => {
    if (BACKEND !== 'supabase') return;
    if (tagsLoadedFromSupabase) return;
    let alive = true;
    (async () => {
      try {
        try {
          const [cachedTags, cachedHabitTagMappings] = await Promise.all([
            todoLocalRepo.listTags(),
            todoLocalRepo.listHabitTagMapRows(),
          ]);
          habitTagMapState = cachedHabitTagMappings
            .filter(mapping => !mapping.is_deleted)
            .map(({ habit_id, tag_id }) => ({ habit_id, tag_id }));
          if (alive && cachedTags.length > 0) {
            setTagsStore(cachedTags);
          } else if (alive) {
            emit();
          }
        } catch (cacheReadError) {
          console.error('Failed to load tags from IndexedDB:', cacheReadError);
        }

        const tagCursor = await todoLocalRepo.getSyncCursor(TODO_SYNC_STATE_KEYS.todoTagsLastSyncedAt);
        const rows = tagCursor
          ? await tagRepo.listTagsModifiedAfter(tagCursor)
          : await tagRepo.listTags();
        const habitTagMapCursor = await todoLocalRepo.getSyncCursor(TODO_SYNC_STATE_KEYS.habitTagMapLastSyncedAt);
        const habitTagMappings = habitTagMapCursor
          ? await habitTagRepo.listMappingsModifiedAfter(habitTagMapCursor)
          : await habitTagRepo.listMappings();
        try {
          await todoLocalRepo.upsertTags(rows);
          await todoLocalRepo.setTodoTagsSyncCursorFromRows(rows);
          await todoLocalRepo.applyHabitTagMapSync(habitTagMappings);
        } catch (cacheError) {
          console.error('Failed to cache tags in IndexedDB:', cacheError);
        }
        if (!alive) return;
        tagsLoadedFromSupabase = true;
        habitTagMapState = (await todoLocalRepo.listHabitTagMapRows())
          .filter(mapping => !mapping.is_deleted)
          .map(({ habit_id, tag_id }) => ({ habit_id, tag_id }));
        const latestTags = await todoLocalRepo.listTags();
        if (!alive) return;
        setTagsStore(latestTags);
      } catch (e) {
        console.error('Failed to load tags from Supabase:', e);
      }
    })();
    return () => { alive = false; };
  }, [syncRequest]);

  const activeTags = useMemo(() => {
    return tags
      .filter(tag => !tag.is_deleted)
      .slice()
      .sort((a, b) => {
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
  }, [tags]);

  const createNewTag = useCallback(async (title: string, body?: string): Promise<TodoTagEntity> => {
    const existingTag = activeTags.find(tag => tag.title.toLowerCase() === title.toLowerCase());
    if (existingTag) return existingTag;

    const nextSortOrder = Math.max(0, ...activeTags.map(t => t.sort_order ?? 0)) + 1000;
    if (BACKEND === 'supabase') {
      try {
        const saved = await tagRepo.createTag({ title, body, sort_order: nextSortOrder });
        cacheTags([saved]);
        setTagsStore(prev => [...prev, saved]);
        return saved;
      } catch (e) {
        console.error('Failed to create tag on Supabase:', e);
        throw e;
      }
    }

    const newTag = { ...createTag(title, body), sort_order: nextSortOrder };
    setTagsStore(prev => [...prev, newTag]);
    return newTag;
  }, [activeTags]);

  const reorderTags = useCallback(async (sourceId: string, targetId: string, insertAfter: boolean) => {
    const ordered = [...tags]
      .filter(t => !t.is_deleted)
      .sort((a, b) => {
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
    const sourceIndex = ordered.findIndex(t => t.id === sourceId);
    const targetIndex = ordered.findIndex(t => t.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    if (sourceIndex === targetIndex) return;

    const next = [...ordered];
    const [moved] = next.splice(sourceIndex, 1);
    const insertionIndex = (() => {
      const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      return insertAfter ? adjustedTargetIndex + 1 : adjustedTargetIndex;
    })();
    next.splice(insertionIndex, 0, moved);

    const prevNeighbor = insertionIndex > 0 ? next[insertionIndex - 1] : undefined;
    const nextNeighbor = insertionIndex < next.length - 1 ? next[insertionIndex + 1] : undefined;
    const prevOrder = prevNeighbor?.sort_order ?? 0;
    const nextOrder = nextNeighbor?.sort_order;
    const updatedAt = new Date().toISOString();

    const shouldRenumber = (() => {
      if (nextNeighbor && nextOrder !== undefined && nextOrder - prevOrder <= 1) return true;
      if (!prevNeighbor && nextNeighbor && nextOrder !== undefined && nextOrder <= 1000) return true;
      return false;
    })();

    if (shouldRenumber) {
      const renumbered = next.map((t, i) => ({
        ...t,
        sort_order: (i + 1) * 1000,
        modified_at: updatedAt,
      }));
      setTagsStore(prev => prev.map(t => {
        const r = renumbered.find(x => x.id === t.id);
        return r ? { ...t, sort_order: r.sort_order, modified_at: r.modified_at } : t;
      }));
      cacheTags(renumbered);
      if (BACKEND === 'supabase') {
        await Promise.all(renumbered.map(t => tagRepo.updateTag(t.id, { sort_order: t.sort_order })));
      }
      return;
    }

    const computedSortOrder = (() => {
      if (!prevNeighbor) {
        if (!nextNeighbor || nextOrder === undefined) return 1000;
        return nextOrder - 1000;
      }
      if (!nextNeighbor || nextOrder === undefined) return prevOrder + 1000;
      return Math.floor(prevOrder + (nextOrder - prevOrder) / 2);
    })();

    setTagsStore(prev => prev.map(t => t.id === moved.id
      ? { ...t, sort_order: computedSortOrder, modified_at: updatedAt }
      : t
    ));
    cacheTags([{ ...moved, sort_order: computedSortOrder, modified_at: updatedAt }]);
    if (BACKEND === 'supabase') {
      await tagRepo.updateTag(moved.id, { sort_order: computedSortOrder });
    }
  }, [tags]);

  const updateTag = useCallback((id: string, updates: Partial<TodoTagEntity>) => {
    const updatedAt = new Date().toISOString();
    const existingTag = tagsState.find(tag => tag.id === id);
    const updatedTag = existingTag ? {
      ...existingTag,
      ...updates,
      modified_at: updatedAt,
    } : null;

    setTagsStore(prev => prev.map(tag => tag.id === id
      ? { ...tag, ...updates, modified_at: updatedAt }
      : tag
    ));
    if (updatedTag) {
      cacheTags([updatedTag]);
    }
    if (updates.is_deleted) {
      habitTagMapState = habitTagMapState.filter(mapping => mapping.tag_id !== id);
      saveHabitTagMapToStorage(habitTagMapState);
      emit();
    }
    if (BACKEND === 'supabase') {
      tagRepo.updateTag(id, updates).catch((e) => console.error('Failed to update tag on Supabase:', e));
    }
  }, []);

  const deleteTag = useCallback((id: string) => {
    updateTag(id, { is_deleted: true });
    if (BACKEND === 'supabase') {
      tagRepo.softDeleteTag(id).catch((e) => console.error('Failed to delete tag on Supabase:', e));
    }
  }, [updateTag]);

  const getTagById = useCallback((id: string): TodoTagEntity | undefined => {
    return tags.find(tag => tag.id === id && !tag.is_deleted);
  }, [tags]);

  const getTagsByIds = useCallback((ids: string[]): TodoTagEntity[] => {
    return tags.filter(tag => ids.includes(tag.id) && !tag.is_deleted);
  }, [tags]);

  const getHabitTagIds = useCallback((habitId: string): string[] => {
    const activeTagIds = new Set(tags.filter(tag => !tag.is_deleted).map(tag => tag.id));
    return habitTagMapState
      .filter(mapping => mapping.habit_id === habitId && activeTagIds.has(mapping.tag_id))
      .map(mapping => mapping.tag_id);
  }, [tags]);

  const setHabitTagIds = useCallback((habitId: string, tagIds: string[]) => {
    const unique = Array.from(new Set(tagIds));
    habitTagMapState = [
      ...habitTagMapState.filter(mapping => mapping.habit_id !== habitId),
      ...unique.map(tagId => ({ habit_id: habitId, tag_id: tagId }))
    ];
    saveHabitTagMapToStorage(habitTagMapState);
    emit();

    if (BACKEND === 'supabase') {
      habitTagRepo
        .setHabitTags(habitId, unique)
        .catch((e) => console.error('Failed to update habit tags on Supabase:', e));
    }
  }, []);

  return {
    activeTags,
    createNewTag,
    reorderTags,
    updateTag,
    deleteTag,
    getTagById,
    getTagsByIds,
    getHabitTagIds,
    setHabitTagIds,
    refreshFromServer,
  };
};
